// ============================================================
// Ticket Template Renderer
// Converts a block-based template + ticket data into HTML.
// Used by:
//   • ReceiptPreview.printFallback (window.open primary path — UNCHANGED delivery)
//   • AdminTicketDesigner live preview
//
// This module NEVER performs any print action itself.
// It only produces an HTML string that the existing print flow renders.
// ============================================================

import { formatCurrency, formatDateTime } from './helpers';

const SIZE_MAP = {
  xs: 10,
  small: 11,
  medium: 13,
  large: 17,
  xl: 22,
};

const HIGHLIGHT_MAP = {
  xs: 14,
  small: 16,
  medium: 20,
  large: 26,
  xl: 32,
};

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const styleFor = (block) => {
  const fs = block.highlight ? (HIGHLIGHT_MAP[block.size] || 20) : (SIZE_MAP[block.size] || 13);
  const parts = [`font-size:${fs}px`];
  if (block.style === 'bold' || block.style === 'bold_centered' || block.highlight) parts.push('font-weight:900');
  if (block.style === 'centered' || block.style === 'bold_centered' || block.highlight) parts.push('text-align:center');
  if (block.margin_top) parts.push(`margin-top:${block.margin_top}px`);
  if (block.margin_bottom) parts.push(`margin-bottom:${block.margin_bottom}px`);
  parts.push('line-height:1.15');
  if (block.highlight) parts.push('letter-spacing:1px');
  return parts.join(';');
};

const dividerHtml = (kind) => {
  if (kind === 'thin') return '<hr style="border:none;border-top:1px solid #000;margin:2px 0"/>';
  if (kind === 'dashed') return '<hr style="border:none;border-top:1px dashed #000;margin:2px 0"/>';
  return '';
};

const labelPart = (block) => (block.label ? `<span style="font-size:${Math.max(9, (SIZE_MAP[block.size] || 13) - 3)}px;letter-spacing:1px;">${esc(block.label)}${block.highlight ? '<br/>' : ': '}</span>` : '');

const formatDurationMin = (min) => {
  if (min == null) return '';
  const m = Math.round(Number(min));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h > 0 ? `${h}h ${r}min` : `${r}min`;
};

const renderBlockContent = (block, td, header, footer) => {
  switch (block.type) {
    case 'logo':
      if (!header?.show_logo || !header?.logo_url) return '';
      return `<img src="${esc(header.logo_url)}" alt="logo" style="max-height:60px;max-width:100%;object-fit:contain;"/>`;
    case 'business_name':
      return esc(header?.business_name || td.business_name || 'ESTACIONAMIENTO');
    case 'address':
      return esc(header?.address || '');
    case 'separator':
      return '&nbsp;';
    case 'ticket_id':
      return `${labelPart(block)}<span>${esc(td.ticket_id || '')}</span>`;
    case 'plate':
      return `${labelPart(block)}<span>${esc(td.plate_number || '')}</span>`;
    case 'space':
      return `${labelPart(block)}<span>${esc(td.space_label || '')}</span>`;
    case 'vehicle_type':
      return `${labelPart(block)}<span>${esc(td.vehicle_type || '')}</span>`;
    case 'entry_date':
      return `${labelPart(block)}<span>${esc(formatDateTime(td.entry_time))}</span>`;
    case 'exit_date':
      if (!td.exit_time) return '';
      return `${labelPart(block)}<span>${esc(formatDateTime(td.exit_time))}</span>`;
    case 'duration':
      if (td.total_minutes == null) return '';
      return `${labelPart(block)}<span>${esc(formatDurationMin(td.total_minutes))}</span>`;
    case 'charges_detail':
      if (!Array.isArray(td.breakdown) || td.breakdown.length === 0) return '';
      return (block.label ? `<div style="font-weight:bold">${esc(block.label)}</div>` : '') +
        td.breakdown.map((it) => `<div style="display:flex;justify-content:space-between"><span>${esc(it.description)}</span><span>${esc(formatCurrency(it.amount))}</span></div>`).join('');
    case 'products': {
      const products = (td.extras_breakdown || []).filter((e) => (e.category || 'product') === 'product');
      if (!products.length) return '';
      return (block.label ? `<div style="font-weight:bold">${esc(block.label)}</div>` : '') +
        products.map((p) => `<div style="display:flex;justify-content:space-between"><span>${esc(p.item_name || p.name)}${p.quantity > 1 ? ' x' + p.quantity : ''}</span><span>${esc(formatCurrency(p.line_total || p.amount || 0))}</span></div>`).join('');
    }
    case 'services': {
      const services = (td.extras_breakdown || []).filter((e) => (e.category || 'product') === 'service');
      if (!services.length) return '';
      return (block.label ? `<div style="font-weight:bold">${esc(block.label)}</div>` : '') +
        services.map((s) => `<div style="display:flex;justify-content:space-between"><span>${esc(s.item_name || s.name)}${s.quantity > 1 ? ' x' + s.quantity : ''}</span><span>${esc(formatCurrency(s.line_total || s.amount || 0))}</span></div>`).join('');
    }
    case 'bathrooms': {
      const baths = (td.extras_breakdown || []).filter((e) => (e.item_name || e.name || '').toLowerCase().includes('baño'));
      if (!baths.length) return '';
      return (block.label ? `<div style="font-weight:bold">${esc(block.label)}</div>` : '') +
        baths.map((s) => `<div style="display:flex;justify-content:space-between"><span>${esc(s.item_name || s.name)}${s.quantity > 1 ? ' x' + s.quantity : ''}</span><span>${esc(formatCurrency(s.line_total || s.amount || 0))}</span></div>`).join('');
    }
    case 'subtotal':
      if (td.parking_amount == null) return '';
      return `${labelPart(block)}<span>${esc(formatCurrency(td.parking_amount))}</span>`;
    case 'total':
      if (td.total_price == null) return '';
      return `${labelPart(block)}<span>${esc(formatCurrency(td.total_price))}</span>`;
    case 'payment_method': {
      if (!td.payment_method) return '';
      const pm = td.payment_method === 'cash' ? 'EFECTIVO' : td.payment_method === 'card_transfer' ? 'TARJETA / TRANSFERENCIA' : '';
      if (!pm) return '';
      const cash = td.payment_method === 'cash' && td.cash_received != null
        ? `<div style="font-size:10px">Recibido: ${esc(formatCurrency(td.cash_received))}${td.cash_change != null ? ' · Vuelto: ' + esc(formatCurrency(td.cash_change)) : ''}</div>`
        : '';
      return `${labelPart(block)}<span>${pm}</span>${cash}`;
    }
    case 'barcode':
      // barcode SVG is injected by ReceiptPreview via placeholder replacement
      return `<div class="__BARCODE_SLOT__" data-tid="${esc(td.ticket_id || '')}"></div>`;
    case 'phone':
      return footer?.phone ? `${labelPart(block)}<span>${esc(footer.phone)}</span>` : '';
    case 'website':
      return footer?.website ? esc(footer.website) : '';
    case 'social':
      return footer?.social ? esc(footer.social) : '';
    case 'footer_message':
      return esc(footer?.message || '');
    default:
      return '';
  }
};

/**
 * Render the ticket blocks into a body HTML fragment.
 * Injects a barcode placeholder that ReceiptPreview will swap with the pre-rendered <svg>.
 */
export function renderTicketBody(template, ticketData) {
  if (!template || !Array.isArray(template.blocks)) return '';
  const header = template.header || {};
  const footer = template.footer || {};
  const barcodeMode = template.barcode_mode || 'barcode';

  const parts = template.blocks
    .filter((b) => b.visible !== false)
    .filter((b) => !(b.type === 'barcode' && barcodeMode === 'hidden'))
    .map((b) => {
      const content = renderBlockContent(b, ticketData, header, footer);
      if (!content || content === '&nbsp;' && b.type !== 'separator') return dividerHtml(b.divider);
      return `<div style="${styleFor(b)}">${content}</div>${dividerHtml(b.divider)}`;
    });

  return parts.join('\n');
}

/**
 * Full print HTML — 80mm thermal-friendly. Kept structurally IDENTICAL to the working
 * printFallback template (only the body is now driven by the template blocks).
 * @param {object} template Active ticket template (from /api/ticket-templates/{kind})
 * @param {object} ticketData Ticket payload
 * @param {string} barcodeSvgMarkup Serialized SVG of the pre-rendered barcode
 */
export function buildTicketPrintHTML(template, ticketData, barcodeSvgMarkup = '') {
  const body = renderTicketBody(template, ticketData);
  // Inject barcode SVG (or ticket id text fallback) into the placeholder
  const bcMode = template?.barcode_mode || 'barcode';
  const barcodeReplacement = (bcMode === 'hidden')
    ? ''
    : `<div style="text-align:center">${barcodeSvgMarkup || ''}<div style="font-family:'Courier New',monospace;font-size:11px;letter-spacing:1px">${esc(ticketData.ticket_id || '')}</div></div>`;
  const finalBody = body.replace(/<div class="__BARCODE_SLOT__"[^>]*><\/div>/g, barcodeReplacement);

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Ticket ${esc(ticketData.ticket_id || '')}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { width: 100%; height: auto; background: #fff; color: #000; }
  body {
    font-family: 'Courier New', Courier, monospace;
    max-width: 300px;
    margin: 0 auto;
    padding: 8px 4px;
    line-height: 1.3;
  }
  hr { color: #000; }
  @media print {
    @page { size: 80mm auto; margin: 2mm; }
    html, body { width: 80mm; }
    body { max-width: none; width: 100%; padding: 0; }
    .no-print { display: none !important; }
  }
  .print-btn {
    display: block; margin: 16px auto; padding: 12px 32px;
    font-size: 16px; font-family: sans-serif;
    background: #16A34A; color: #fff; border: none; border-radius: 8px; cursor: pointer;
  }
</style>
</head>
<body>
${finalBody}
<button class="print-btn no-print" onclick="window.print()">Imprimir</button>
<script>
  setTimeout(function() { window.print(); }, 500);
</script>
</body>
</html>`;
}
